import { createTheme } from "@mui/material";
import { grey, red } from "@mui/material/colors";
import { blue, pink } from "./colors";
import React from "react";

const paletteError = {
    light: red[200],
    main: red[500],
    dark: red[700],
};
const palettePrimary = pink;
const paletteSecondary = blue;

export const VPUITheme = createTheme({
    palette: {
        error: paletteError,
        primary: palettePrimary,
        secondary: paletteSecondary,
    },
    typography: {
        fontFamily: "VPSans",
        h2: {
            fontWeight: "bold",
            fontSize: "30px",
            color: pink[500],
        },
        h3: {
            fontWeight: "bold",
            fontSize: "20px",
            color: "default",
        },
        h4: {
            fontWeight: "bold",
            fontSize: "12px",
            color: "#637381",
        },
        subtitle1: {
            fontWeight: "normal",
            fontSize: "12px",
        },
        subtitle2: {
            fontWeight: "200",
            fontSize: "12px",
        },
    },
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    boxShadow: "none",
                },
                text: {
                    color: "#637381",
                },
                contained: {
                    "boxShadow": "none",
                    "&:hover": {
                        boxShadow: "none",
                    },
                },
                outlined: {
                    borderColor: "#EEEEEE",
                },
                sizeLarge: {
                    fontSize: "16px",
                    paddingTop: "12px",
                    paddingBottom: "12px",
                },
                sizeSmall: {
                    minWidth: 0,
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    overflow: "visible",
                },
            },
        },
        MuiCardActions: {
            styleOverrides: {
                root: {
                    marginTop: 0,
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                elevation0: {
                    borderWidth: "1px",
                    borderColor: "#EEEEEE",
                    borderStyle: "solid",
                },
                elevation10: {
                    borderWidth: "1px",
                    borderColor: grey.A100,
                    borderStyle: "solid",
                    boxShadow: "none",
                },
            },
        },
        MuiPopover: {
            styleOverrides: {
                paper: {
                    borderWidth: "1px",
                    borderColor: "#DBDBDB",
                    borderStyle: "solid",
                },
            },
        },
        MuiTableHead: {
            styleOverrides: {
                root: {
                    borderTopColor: "#E8E8E8",
                    borderTopStyle: "solid",
                    borderTopWidth: "2px",
                    borderBottomColor: "#E8E8E8",
                    borderBottomStyle: "solid",
                    borderBottomWidth: "2px",
                    boxShadow: "none",
                },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                root: {
                    borderColor: "#EEEEEE",
                    fontFamily: "Roboto",
                },
                body: {
                    color: "#404040",
                },
            },
        },
        MuiCardHeader: {
            styleOverrides: {
                root: {
                    borderBottomWidth: "1px",
                    borderBottomColor: "#E8E8E8",
                    borderBottomStyle: "solid",
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    marginLeft: "2px",
                    marginRight: "2px",
                },
                sizeSmall: {
                    fontSize: "12px",
                },
            },
        },
        MuiDialog: {
            defaultProps: {
                style: {
                    "--palette-color-error-light": paletteError.light,
                    "--palette-color-error-dark": paletteError.dark,
                    "--palette-color-error": paletteError.main,
                    "--palette-color-primary": palettePrimary[500],
                    "--palette-color-secondary": paletteSecondary[500],
                } as React.CSSProperties,
            },
        },
        MuiDialogTitle: {
            styleOverrides: {
                root: {
                    borderBottomWidth: "1px",
                    borderBottomColor: "#E8E8E8",
                    borderBottomStyle: "solid",
                },
            },
        },
        MuiDialogContent: {
            styleOverrides: {
                root: {
                    marginTop: "10px",
                },
            },
        },
        MuiDialogActions: {
            styleOverrides: {
                root: {
                    borderTopWidth: "1px",
                    borderTopColor: "#E8E8E8",
                    borderTopStyle: "solid",
                    paddingBottom: "12px",
                    paddingLeft: "24px",
                    paddingRight: "24px",
                    paddingTop: "12px",
                },
            },
        },
        MuiFormLabel: {
            styleOverrides: {
                root: {
                    "color": "#444444",
                    "&.Mui-focused": {
                        color: "#444",
                    },
                },
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: grey[700],
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderWidth: 1,
                        borderColor: blue[500],
                    },
                    "&.Mui-disabled": {
                        backgroundColor: grey[100],
                    },
                    "&.Mui-disabled .MuiOutlinedInput-notchedOutline": {
                        borderColor: grey[300],
                    },
                },
                notchedOutline: {
                    borderColor: grey[300],
                },
            },
        },
        MuiSelect: {
            defaultProps: {
                MenuProps: {
                    anchorOrigin: {
                        vertical: "bottom",
                        horizontal: "center",
                    },
                    elevation: 0,
                    MenuListProps: {
                        style: {
                            paddingTop: "0",
                        },
                    },
                    style: {
                        marginTop: "8px",
                    },
                    transformOrigin: {
                        vertical: "top",
                        horizontal: "center",
                    },
                },
            },
            styleOverrides: {
                iconOutlined: {
                    right: "9px",
                    color: grey[600],
                },
            },
        },
        MuiTableSortLabel: {
            styleOverrides: {
                root: {
                    color: "#637381",
                    fontSize: "12px",
                    fontWeight: "bold",
                    textTransform: "uppercase",
                },
            },
        },
    },
});

VPUITheme.typography.fontFamily = "VPSans";
VPUITheme.typography.body1.fontFamily = "VPSans";
VPUITheme.typography.body2.fontFamily = "VPSans";

VPUITheme.typography.h6.fontFamily = "VPSans";
VPUITheme.typography.h6.color = "default";
VPUITheme.typography.h6.textAlign = "center";
